import React, { useState } from 'react';
import { Lightbulb, Book, Bug, Heart, Send, Loader2, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/backendService';

const FeedbackPage: React.FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [priority, setPriority] = useState('Would be nice');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const categories = [
        {
            id: 'Share a Feature Idea',
            title: 'Share a Feature Idea',
            icon: <Lightbulb className="w-8 h-8 text-earth-600 mb-2" />,
            example: '"I\'d love a tracker for my daily Ayurvedic morning routine!"'
        },
        {
            id: 'Suggest a Wisdom Source',
            title: 'Suggest a Wisdom Source',
            icon: <Book className="w-8 h-8 text-sage-600 mb-2" />,
            example: '"Can you add [Specific Naturopathy Book] to your knowledge library?"'
        },
        {
            id: 'Report a Technical Issue',
            title: 'Report a Technical Issue',
            icon: <Bug className="w-8 h-8 text-red-500 mb-2" />,
            example: '"The Yoga Aid card isn\'t loading on my mobile screen."'
        },
        {
            id: 'General Feedback & Stories',
            title: 'General Feedback & Stories',
            icon: <Heart className="w-8 h-8 text-pink-500 mb-2" />,
            example: 'Tell us how NatureNani has impacted your wellness learning journey.'
        }
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!agreedToTerms) {
            alert("Please agree to the Feedback Submission Terms and Privacy Policy before submitting.");
            return;
        }

        setIsSending(true);
        setError(null);

        try {
            const { error: funcError } = await supabase.functions.invoke('send-contact-email', {
                body: {
                    source: 'feedback',
                    name: name,
                    email: email,
                    category: selectedCategory,
                    priority: priority,
                    message: message
                }
            });

            if (funcError) throw funcError;

            setIsSubmitted(true);
            // Clear form
            setName('');
            setEmail('');
            setMessage('');
            setSelectedCategory('');
        } catch (err: any) {
            console.error('Error submitting feedback:', err);
            setError('Failed to submit feedback. Please try again or email us at support@naturenani.com.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12 font-sans text-stone-800">
            {/* Header Section */}
            <section className="mb-12 text-center">
                <h1 className="text-4xl md:text-5xl font-serif font-bold text-sage-900 mb-4 flex items-center justify-center gap-3">
                    <span className="text-4xl">🌿</span> Shape the Future of NatureNani
                </h1>
                <h2 className="text-2xl font-bold text-earth-800 mb-6">Your Wisdom, Our Growth</h2>
                <div className="max-w-3xl mx-auto text-stone-600 leading-relaxed text-lg">
                    NatureNani is built on the belief that collective wisdom leads to better healing. As we bridge ancient traditions with modern technology, your experience is our most valuable guide. Whether you've found a bug, have a brilliant feature idea, or want to suggest a specific ancient text for our library—we are listening.
                </div>
            </section>

            {/* Success State */}
            {isSubmitted ? (
                <div className="bg-sage-50 border border-sage-200 rounded-2xl p-12 text-center my-12 shadow-sm">
                    <h2 className="text-3xl font-serif text-sage-900 mb-4">Namaste!</h2>
                    <p className="text-lg text-stone-700 mb-8 italic">
                        "Your feedback has been received and added to our archives. Your voice is now part of NatureNani's growth."
                    </p>
                    <div className="flex justify-center gap-4">
                        <button
                            onClick={() => setIsSubmitted(false)}
                            className="bg-sage-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-sage-700 transition shadow-md"
                        >
                            Send Another Response
                        </button>
                        <Link to="/" className="bg-white text-sage-700 border border-sage-300 px-6 py-3 rounded-lg font-bold hover:bg-sage-50 transition shadow-sm">
                            Return Home
                        </Link>
                    </div>
                </div>
            ) : (
                <>
                    {/* Categories Section */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-sage-900 mb-2 border-b border-sage-100 pb-2">How Can We Improve Your Journey?</h2>
                        <p className="text-stone-600 mb-6 italic">Please select a category so we can direct your feedback to the right team.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`text-left p-6 rounded-xl border-2 transition-all duration-200 h-full flex flex-col items-start hover:shadow-md
                                        ${selectedCategory === cat.id
                                            ? 'border-sage-500 bg-sage-50 ring-2 ring-sage-200 ring-offset-2 scale-105'
                                            : 'border-stone-200 bg-white hover:border-sage-300'
                                        }`}
                                >
                                    {cat.icon}
                                    <h3 className="font-bold text-lg text-stone-800 mb-2">{cat.title}</h3>
                                    <p className="text-xs text-stone-500 leading-relaxed italic">{cat.example}</p>
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* Form Section */}
                    <section className="bg-white rounded-2xl shadow-lg border border-sage-100 p-8 mb-12 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-sage-400 to-earth-500"></div>
                        <h2 className="text-2xl font-bold text-sage-900 mb-6">Submit Your Feedback</h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-stone-700 mb-2">Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your name"
                                        className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition"
                                        required
                                        disabled={isSending}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-stone-700 mb-2">Email <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition"
                                        required
                                        disabled={isSending}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-stone-700 mb-2">Feedback Category</label>
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition bg-white"
                                    required
                                >
                                    <option value="" disabled>Select a category</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-stone-700 mb-2">Your Message</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={5}
                                    placeholder="Tell us more about your feedback..."
                                    className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none transition resize-none"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-stone-700 mb-3 border-b border-stone-100 pb-2">Priority</label>
                                <div className="flex flex-wrap gap-6 mt-3">
                                    {['Just a thought', 'Would be nice', 'Essential for me'].map((opt) => (
                                        <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                                            <input
                                                type="radio"
                                                name="priority"
                                                value={opt}
                                                checked={priority === opt}
                                                onChange={(e) => setPriority(e.target.value)}
                                                className="w-4 h-4 text-sage-600 border-gray-300 focus:ring-sage-500"
                                            />
                                            <span className="text-stone-700 group-hover:text-sage-700 transition-colors">{opt}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-sage-50/50 p-6 rounded-xl border border-sage-100 mt-8">
                                <h3 className="font-bold text-sage-900 mb-3 flex items-center gap-2">📋 Feedback Submission Terms</h3>
                                <p className="text-xs text-stone-600 mb-3">By submitting this feedback, you acknowledge and agree that:</p>
                                <ul className="space-y-2 text-xs text-stone-600 mb-6 list-none">
                                    <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span> You grant NatureNani (Opound LLC) a worldwide, royalty-free, perpetual, non-exclusive license to use, modify, implement, and incorporate your feedback, suggestions, or ideas without compensation or attribution</li>
                                    <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span> Your submission does not violate any third-party intellectual property rights or confidentiality obligations</li>
                                    <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span> We may already be developing similar features independently</li>
                                    <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span> We are under no obligation to implement, acknowledge, or respond to any submission</li>
                                    <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span> No confidential relationship is established by your submission</li>
                                </ul>

                                <h3 className="font-bold text-sage-900 mb-2 flex items-center gap-2">🔒 Privacy Notice</h3>
                                <p className="text-xs text-stone-600 mb-4">
                                    By submitting this form, you consent to the collection and use of your information as described in our Privacy Policy. We will use your contact information solely to respond to your inquiry and for customer service purposes. You may request deletion of your information at any time by contacting privacy@naturenani.com.
                                </p>

                                <label className="flex items-start gap-3 cursor-pointer bg-white p-4 rounded-lg border border-sage-200 hover:border-sage-400 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        className="mt-1 w-4 h-4 text-sage-600 rounded border-gray-300 focus:ring-sage-500"
                                        required
                                    />
                                    <span className="text-sm font-bold text-sage-800">I have read and agree to the Feedback Submission Terms and Privacy Policy</span>
                                </label>
                            </div>

                            {error && (
                                <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm border border-red-100">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSending}
                                className="w-full bg-sage-800 text-white font-bold py-4 rounded-xl hover:bg-sage-900 transition flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-lg disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isSending ? (
                                    <>
                                        <Loader2 size={24} className="animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Send size={24} />
                                        Send to Nani's Inbox
                                    </>
                                )}
                            </button>
                        </form>
                    </section>
                </>
            )}

            {/* Roadmap Section */}
            <section className="bg-gradient-to-br from-earth-50 to-sage-50 rounded-2xl p-8 lg:p-10 mb-12 border border-earth-100 shadow-sm">
                <h2 className="text-2xl font-bold text-earth-900 mb-4">Our Current Focus Areas</h2>
                <p className="text-earth-700 italic mb-6">Transparency builds trust. Here are some areas we're exploring for potential future development (timelines and specific features subject to change based on technical feasibility and community needs):</p>
                <ul className="space-y-4">
                    <li className="flex items-start gap-4 p-4 bg-white/60 rounded-xl backdrop-blur-sm border border-white/50">
                        <span className="text-2xl bg-white p-2 rounded-lg shadow-sm">✨</span>
                        <div className="text-earth-900 mt-1">
                            <strong className="block mb-1 text-lg">Enhanced Wellness Insights</strong>
                            <span className="text-stone-600">Exploring ways to provide deeper educational insights based on user-provided health information</span>
                        </div>
                    </li>
                    <li className="flex items-start gap-4 p-4 bg-white/60 rounded-xl backdrop-blur-sm border border-white/50">
                        <span className="text-2xl bg-white p-2 rounded-lg shadow-sm">🧘‍♀️</span>
                        <div className="text-earth-900 mt-1">
                            <strong className="block mb-1 text-lg">Expanded Yoga Aid Resources</strong>
                            <span className="text-stone-600">Growing our library of educational protocols for wellness support</span>
                        </div>
                    </li>
                    <li className="flex items-start gap-4 p-4 bg-white/60 rounded-xl backdrop-blur-sm border border-white/50">
                        <span className="text-2xl bg-white p-2 rounded-lg shadow-sm">🍵</span>
                        <div className="text-earth-900 mt-1">
                            <strong className="block mb-1 text-lg">Personalized Nutri Heal Guidance</strong>
                            <span className="text-stone-600">Developing region and season-aware dietary education based on traditional principles</span>
                        </div>
                    </li>
                </ul>
            </section>

            {/* Disclaimer Section */}
            <section className="bg-amber-50 rounded-2xl p-6 mb-12 border border-amber-200">
                <h2 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">⚠️ Important Educational Disclaimer</h2>
                <p className="text-amber-800 text-sm">
                    NatureNani is an educational platform only. We do not provide medical advice, diagnosis, or treatment. All content is for informational purposes and should not replace consultation with qualified healthcare professionals. See our full <Link to="/disclaimer" className="underline font-bold hover:text-amber-950">Disclaimer</Link> for complete terms.
                </p>
            </section>

            {/* Founder Note & Contact Links */}
            <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="bg-sage-800 text-white rounded-2xl p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 opacity-10 text-9xl">🌿</div>
                    <h2 className="text-xl font-bold mb-4 relative z-10">A Note from the Founder</h2>
                    <p className="font-serif italic text-sage-100 text-lg leading-relaxed relative z-10 mb-6">
                        "Every piece of feedback is read by me personally. Your insights help us restore collective wisdom about Natural Intelligence while maintaining the authenticity and accuracy that ancient traditions deserve. Thank you for being part of this journey."
                    </p>
                    <p className="font-bold relative z-10 text-sage-50">— Navilla Bagga, Founder</p>
                </div>

                <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm space-y-4">
                    <h2 className="text-xl font-bold text-sage-900 mb-4 border-b border-stone-100 pb-2">Looking for Something Else?</h2>

                    <div>
                        <span className="block font-bold text-stone-800 text-sm">Need Partnership or Press Inquiries?</span>
                        <Link to="/contact" className="text-sage-600 hover:text-sage-800 hover:underline font-medium text-sm">Visit our Contact Page</Link>
                    </div>

                    <div>
                        <span className="block font-bold text-stone-800 text-sm mt-3">Questions about Your Account?</span>
                        <a href="mailto:support@naturenani.com" className="text-sage-600 hover:text-sage-800 hover:underline font-medium text-sm">Email support@naturenani.com</a>
                    </div>

                    <div>
                        <span className="block font-bold text-stone-800 text-sm mt-3">Privacy Concerns?</span>
                        <a href="mailto:privacy@naturenani.com" className="text-sage-600 hover:text-sage-800 hover:underline font-medium text-sm">Email privacy@naturenani.com</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedbackPage;
